import Controller from '@ember/controller';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { toLatLngObject } from 'garaje/helpers/resource-coords';
import { getIdFromURN } from 'garaje/models/generic-resource';
import type {
  ResourceIconConfig,
  ResourceMapPoint,
  ResourceMapPointPolygon,
} from 'garaje/pods/components/resource-map/base-map/component';
import type MetricsService from 'garaje/services/metrics';
import { AvailabilityStatus } from 'garaje/types/maps/live-map/LiveMapServiceV2';
import type { Floor, Neighborhood, Amenity, Resource, ResourceType } from 'garaje/types/maps/live-map/LiveMapServiceV2';
import { buildProfilePictureIcon, buildMonogramIcon, buildResourceIcon } from 'garaje/utils/map/build-map-icons';
import { FEATURE_ICON_MAP } from 'garaje/utils/map/map-icons';
import type { NeighborhoodPolygons } from 'garaje/utils/neighborhood-polygons';
import { getNeighborhoodPolygonsFromResources } from 'garaje/utils/neighborhood-polygons';
import { POLYGON_COLORS, POLYGON_BASE_OPTIONS } from 'garaje/utils/polygon';
import type { Maybe } from 'graphql/jsutils/Maybe';
import type { DivIcon, Map } from 'leaflet';
import { Polygon } from 'leaflet';
import compact from 'lodash/compact';

import type { ExternalMapControllerModelType } from './route';

export default class MapsShowExternalController extends Controller {
  declare model: ExternalMapControllerModelType;
  @service declare router: RouterService;
  @service declare metrics: MetricsService;
  @service declare store: Store;
  @tracked selectedNeighborhoods: Neighborhood[] = [];
  @tracked leafletMap: Maybe<Map> = null;
  @tracked neighborhoodMarginSetting = 0.0265;
  @tracked currentMapFloor: Floor | null =
    this.floors.sort((a, b) => (a.ordinality || 0) - (b.ordinality || 0))[0] || null;
  @tracked mapDimensions: [number, number] = [1200, 800];
  @tracked neighborhoodPolygons: NeighborhoodPolygons = {};
  @tracked currentResources: Resource[] = this.resources.filter(
    (resource) => resource.floorId === this.currentMapFloor?.id,
  );

  @action
  async onMapInsert(): Promise<void> {
    await this.loadNeighborhoodMarginSetting();
    const mapType = this.isDraft ? 'DRAFT' : 'LIVE';
    this.metrics.trackEvent(`EXTERNAL_${mapType}_MAP_VIEWED`);
  }

  @action
  onMapFloorChange(newMapFloor: Floor): void {
    this.currentMapFloor = newMapFloor;
    this.currentResources = this.resources.filter((resource) => resource.floorId === this.currentMapFloor?.id);
  }

  @action
  shareMapDimensions(dimensions: [number, number]): void {
    this.mapDimensions = dimensions;
  }

  async loadNeighborhoodMarginSetting(): Promise<void> {
    const locationId = this.model.locationId;
    if (!locationId) return;

    const deskLocation = this.store.peekRecord('desk-location', locationId);
    if (deskLocation) {
      const setting = deskLocation.neighborhoodSliderSetting;
      if (typeof setting === 'number') {
        this.neighborhoodMarginSetting = setting;
        return;
      }
    }

    try {
      const response = await fetch(`/api/desk-locations/${locationId}`);
      if (!response.ok) return;

      const data = (await response.json()) as { data?: { attributes?: { 'neighborhood-slider-setting'?: number } } };
      const setting = data?.data?.attributes?.['neighborhood-slider-setting'];

      if (typeof setting === 'number') {
        this.neighborhoodMarginSetting = setting;
      }
    } catch {
      // Silently fail - will use default value
    }
  }

  get isDraft(): boolean {
    const currentRoute = this.router.currentRouteName;
    return currentRoute.includes('draft');
  }

  get pageTitle(): string {
    if (this.isDraft) {
      return this.model.draftName || 'Draft Map';
    }
    return this.model.companyName || 'Live Map';
  }

  get floors(): Floor[] {
    return this.model.floors;
  }

  get neighborhoods(): Neighborhood[] {
    return this.model.neighborhoods;
  }

  get amenities(): Amenity[] {
    return this.model.amenities;
  }

  get resources(): Resource[] {
    return this.model.resources;
  }

  get resourceTypes(): ResourceType[] {
    return this.model.resourceTypes;
  }

  get resourceConfig(): ResourceIconConfig {
    const config: ResourceIconConfig = {
      desk: {
        iconOptions: {
          DEFAULT: {
            classNames: ['desk'],
            testAttributeLabel: 'data-test-available-desk',
          },
          AVAILABLE: {
            classNames: ['desk'],
            testAttributeLabel: 'data-test-available-desk',
          },
          UNAVAILABLE: {
            classNames: ['desk', 'is-disabled'],
            testAttributeLabel: 'data-test-disabled-desk',
          },
          OCCUPIED: {
            classNames: ['desk', 'is-disabled'],
            testAttributeLabel: 'data-test-reserved-desk',
          },
        },
      },
      parking: {
        iconOptions: {
          DEFAULT: {
            classNames: ['parking', 'is-disabled'],
            testAttributeLabel: 'data-test-available-parking',
          },
          AVAILABLE: {
            classNames: ['parking'],
            testAttributeLabel: 'data-test-available-parking',
          },
          UNAVAILABLE: {
            classNames: ['parking', 'is-disabled'],
            testAttributeLabel: 'data-test-disabled-parking',
          },
          OCCUPIED: {
            classNames: ['parking', 'is-disabled'],
            testAttributeLabel: 'data-test-reserved-parking',
          },
        },
      },
      room: {
        iconOptions: {
          DEFAULT: {
            classNames: ['room'],
            testAttributeLabel: 'data-test-available-room',
          },
          AVAILABLE: {
            classNames: ['room'],
            testAttributeLabel: 'data-test-available-room',
          },
          UNAVAILABLE: {
            classNames: ['room', 'is-disabled'],
            testAttributeLabel: 'data-test-disabled-room',
          },
          OCCUPIED: {
            classNames: ['room', 'is-unavailable'],
            testAttributeLabel: 'data-test-reserved-room',
          },
        },
        labelOptions: {
          DEFAULT: {
            classNames: ['room', 'feature-label absolute text-center text-cilantro-40 top-6'],
            testAttributeLabel: 'data-test-available-room',
          },
          AVAILABLE: {
            classNames: ['room', 'feature-label absolute text-center text-cilantro-40 top-6'],
            testAttributeLabel: 'data-test-available-room',
          },
          UNAVAILABLE: {
            classNames: ['room', 'feature-label', 'absolute', 'text-center', 'text-red-40', 'top-6'],
            testAttributeLabel: 'data-test-disabled-room',
          },
          OCCUPIED: {
            classNames: ['room', 'feature-label', 'absolute', 'text-center', 'text-red-40', 'top-6'],
            testAttributeLabel: 'data-test-reserved-room',
          },
        },
      },
    };

    Object.entries(FEATURE_ICON_MAP as Record<string, { labelColor: string }>).forEach(([key, iconConstant]) => {
      if (key !== 'room') {
        config[key] = {
          iconOptions: {
            DEFAULT: {
              classNames: [key, iconConstant.labelColor],
              testAttributeLabel: `data-test-${key}`,
            },
          },
        };
      }
    });

    return config;
  }

  getIconForMarker(resource: Resource): DivIcon | undefined {
    const hasReservation = resource.reservedForProfileData != null;
    const hasValidIconFromReservation =
      resource.reservedForProfileData?.avatarUrl != null &&
      !resource.reservedForProfileData?.avatarUrl.includes('gravatar.com');

    const hasAssignment = resource.assignedProfileData != null;
    const hasValidIconFromAssignment =
      resource.assignedProfileData?.avatarUrl != null &&
      !resource.assignedProfileData?.avatarUrl.includes('gravatar.com');

    if (hasReservation && resource.reservedForProfileData != null) {
      return hasValidIconFromReservation
        ? buildProfilePictureIcon(resource.reservedForProfileData.avatarUrl)
        : buildMonogramIcon(resource.reservedForProfileData.name);
    }

    if (hasAssignment && resource.assignedProfileData != null) {
      return hasValidIconFromAssignment
        ? buildProfilePictureIcon(resource.assignedProfileData.avatarUrl)
        : buildMonogramIcon(resource.assignedProfileData.name);
    }

    const resourceType = getIdFromURN(resource.resourceTypeId) as string;
    if (!resourceType) return;

    const kebabCaseResourceType = resourceType.replace(/_/g, '-');
    const allIconOptions = this.resourceConfig[kebabCaseResourceType]?.iconOptions;
    const iconOption = allIconOptions?.[resource.availabilityStatus] || allIconOptions?.['DEFAULT'];
    if (!iconOption) return;

    const allLabelOptions = this.resourceConfig[kebabCaseResourceType]?.labelOptions;
    const labelOption = allLabelOptions?.[resource.availabilityStatus] || allLabelOptions?.['DEFAULT'];
    if (labelOption) {
      labelOption.value = resource.name;
    }

    // Add icon size to the options
    const mapElement = this.leafletMap?.getContainer().querySelector('.leaflet-map');
    const iconSize = mapElement ? Math.min(mapElement.clientWidth, mapElement.clientHeight) / 30 : 32;

    return buildResourceIcon(
      {
        ...iconOption,
        size: iconSize,
      },
      labelOption,
    );
  }

  getResourceTypeLabel(resource: Resource): string {
    const resourceType = getIdFromURN(resource.resourceTypeId) as string;
    if (!resourceType) return '';
    return resourceType.replace(/_/g, ' ');
  }

  getToolTipLabel(resource: Resource): string {
    // Get the profile name from either assignment or reservation
    const profileName = resource.assignedProfileData?.name || resource.reservedForProfileData?.name;

    // If we have a profile name, use it, otherwise use the resource name
    let label = profileName || resource.name;

    // Add neighborhood info if it exists
    if (resource.neighborhoodId) {
      const neighborhood = this.neighborhoods.find((n) => n.id === resource.neighborhoodId);
      if (neighborhood) {
        label += ` \u2022 ${neighborhood.name}`;
      }
    }

    return label;
  }

  get resourceMapPoints(): ResourceMapPoint[] {
    const filteredResources = this.currentResources.filter((resource) => resource.geometry.type === 'POINT');
    return compact(
      filteredResources.map((resource) => {
        if (!resource.geometry.pointGeometry) return;
        const coordinates = resource.geometry.pointGeometry.coordinates;

        const icon = this.getIconForMarker(resource);
        if (!icon) return;

        return {
          resource,
          icon,
          tooltipLabel: this.getToolTipLabel(resource),
          coordinates,
        } as ResourceMapPoint;
      }),
    );
  }

  get resourceMapPointPolygons(): ResourceMapPointPolygon[] {
    const filteredResources = this.currentResources.filter((resource) => resource.geometry.type === 'POLYGON');
    return compact(
      filteredResources.map((resource) => {
        if (!resource.geometry.polygonGeometry) return;
        const coordinates = resource.geometry.polygonGeometry.coordinates;
        const location = coordinates.map((coordinatePair) => toLatLngObject(coordinatePair, this.mapDimensions));

        const colorStyles = this.getPolygonColors(resource);

        const polygon = new Polygon(location, {
          ...colorStyles,
          ...POLYGON_BASE_OPTIONS,
        });

        const icon = this.getIconForMarker(resource);
        if (!icon) return;

        return {
          name: resource.name,
          icon,
          polygon,
        };
      }),
    );
  }

  getPolygonColors(resource: Resource): { color: string; fillColor: string } {
    switch (resource.availabilityStatus) {
      case AvailabilityStatus.AVAILABLE:
        return {
          color: POLYGON_COLORS['enabled'].border,
          fillColor: POLYGON_COLORS['enabled'].fill,
        };
      case AvailabilityStatus.OCCUPIED:
        return {
          color: POLYGON_COLORS['unavailable'].border,
          fillColor: POLYGON_COLORS['unavailable'].fill,
        };
      default:
        return {
          color: POLYGON_COLORS['disabled'].border,
          fillColor: POLYGON_COLORS['disabled'].fill,
        };
    }
  }

  get resourceMapPolygons(): NeighborhoodPolygons {
    const deskResources = this.currentResources.filter((resource) => getIdFromURN(resource.resourceTypeId) === 'desk');
    const mapPolygons = getNeighborhoodPolygonsFromResources(
      deskResources,
      this.mapDimensions,
      this.neighborhoodMarginSetting,
    );
    return this.selectedNeighborhoods.reduce((acc, neighborhood) => {
      acc[neighborhood.id] = mapPolygons[neighborhood.id] || [];
      return acc;
    }, {} as NeighborhoodPolygons);
  }

  @action
  getNeighborhoodColor(neighborhoodId: string): string | undefined {
    const neighborhood = this.neighborhoods.find((neighborhood) => neighborhood.id === neighborhoodId);
    return neighborhood?.displayColor;
  }

  @action
  setSelectedNeighborhoods(neighborhoods: Neighborhood[]): void {
    this.selectedNeighborhoods = neighborhoods;
  }
}
